/**
 * Fiskil API client - calls Gloss API (backend) which proxies to Fiskil.
 * All Fiskil operations are delegated to the backend.
 */

import { inject, Injectable } from "@angular/core";
import { DataRepositoryService } from "../../DataRepository/data-repository.service";
import { GlossSyncService } from "../gloss.sync.service";
import { getFiskilConfig } from "./fiskil.config";
import { ErrorResponse } from "@bitwarden/common/models/response/error.response";
import type {
  FiskilTokenResponse,
  FiskilAuthSessionResponse,
  FiskilAccountsResponse,
  FiskilBalancesResponse,
  FiskilTransactionsResponse,
  FiskilSyncStatusResponse,
  FiskilRevokeConsentsResponse,
  FiskilConsentsListResponse,
} from "./types";

@Injectable({
  providedIn: "root",
})
export class FiskilApi {
  private dataRepositoryService = inject(DataRepositoryService);
  private glossSyncService = inject(GlossSyncService);

  private get glossApiUrl(): string {
    const config = getFiskilConfig();
    const base = config.url;
    const stage = config.apiStage ? `/${config.apiStage}` : "";
    return `${base}${stage}`;
  }

  /**
   * Step 1: Generate token via Gloss API (backend exchanges client_id/secret for Fiskil token).
   */
  async generateToken(): Promise<string> {
    const config = getFiskilConfig();
    const path = config.endpoint.token;

    const response = await this.dataRepositoryService.send(
      "POST",
      path,
      {},
      true,
      true,
      this.glossApiUrl,
    );

    const data = response as FiskilTokenResponse;
    return data.token;
  }

  /**
   * Create auth session via Gloss API (backend creates Fiskil auth session).
   * Backend resolves end_user_id from authenticated user's stored aggregator;
   * if missing or stale, backend self-heals by creating/relinking before creating session.
   */
  async createAuthSession(isFromGuide: boolean = false): Promise<FiskilAuthSessionResponse> {
    const config = getFiskilConfig(isFromGuide);
    const path = config.endpoint.authSession;

    const response = await this.dataRepositoryService.send(
      "POST",
      path,
      {
        cancel_uri: config.cancelUri,
        redirect_uri: config.redirectUri,
      },
      true,
      true,
      this.glossApiUrl,
    );

    return response as FiskilAuthSessionResponse;
  }

  /**
   * Report that user has saved Fiskil data to vault. Clears has_unsaved_synced_data on backend.
   */
  async reportImportComplete(): Promise<{ ok: boolean }> {
    const config = getFiskilConfig();
    const path = config.endpoint.importComplete;

    const response = await this.dataRepositoryService.send(
      "POST",
      path,
      {},
      true,
      true,
      this.glossApiUrl,
    );

    return response as { ok: boolean };
  }

  /**
   * Active Fiskil consents (server filters with ?active=true).
   * On 400 (no aggregator) / 500: fail-open — returns { consents: [] }.
   * Re-throws null/401/403 so auth flows match other authed calls.
   */
  async getActiveConsents(): Promise<FiskilConsentsListResponse> {
    const config = getFiskilConfig();
    const path = `${config.endpoint.consents}?active=true`;
    try {
      const response = (await this.dataRepositoryService.send(
        "GET",
        path,
        null,
        true,
        true,
        this.glossApiUrl,
      )) as FiskilConsentsListResponse;
      return {
        consents: response?.consents ?? [],
        links: response?.links,
      };
    } catch (e: unknown) {
      if (e == null) {
        throw e;
      }
      if (e instanceof ErrorResponse && (e.statusCode === 401 || e.statusCode === 403)) {
        throw e;
      }
      await this.glossSyncService.logError(
        [],
        e instanceof Error ? e : new Error(String(e)),
        "Fiskil GET active consents",
        null,
      );
      return { consents: [] };
    }
  }

  /**
   * Revoke all active Fiskil consents for the user's aggregator.
   * Returns summary: total, revoked, failed.
   */
  async revokeConsents(): Promise<FiskilRevokeConsentsResponse> {
    const config = getFiskilConfig();
    const path = config.endpoint.consents;

    const response = await this.dataRepositoryService.send(
      "DELETE",
      path,
      null,
      true,
      true,
      this.glossApiUrl,
    );

    return response as FiskilRevokeConsentsResponse;
  }

  /**
   * Check if Fiskil data is synced (webhook received).
   */
  async getSyncStatus(): Promise<FiskilSyncStatusResponse> {
    const config = getFiskilConfig();
    const path = config.endpoint.syncStatus;

    const response = await this.dataRepositoryService.send(
      "GET",
      path,
      null,
      true,
      true,
      this.glossApiUrl,
    );

    return response as FiskilSyncStatusResponse;
  }

  /**
   * Step 5a: Fetch accounts via Gloss API (backend fetches from Fiskil).
   */
  async getAccounts(): Promise<FiskilAccountsResponse> {
    const config = getFiskilConfig();
    const path = config.endpoint.accounts;

    const response = await this.dataRepositoryService.send(
      "GET",
      path,
      null,
      true,
      true,
      this.glossApiUrl,
    );

    return response as FiskilAccountsResponse;
  }

  /**
   * Lists Fiskil account_ids for vault-gap checks. Retries once; returns null if still unavailable.
   */
  async listApiAccountIdsWithRetry(maxAttempts = 2, delayMs = 500): Promise<string[] | null> {
    for (let attempt = 0; attempt < maxAttempts; attempt++) {
      try {
        const response = await this.getAccounts();
        return (response?.accounts ?? []).map((a) => a.account_id);
      } catch {
        if (attempt + 1 >= maxAttempts) {
          return null;
        }
        await new Promise((resolve) => setTimeout(resolve, delayMs));
      }
    }
    return null;
  }

  /**
   * Fetch balances via Gloss API (backend fetches from Fiskil).
   */
  async getBalances(): Promise<FiskilBalancesResponse> {
    const config = getFiskilConfig();
    const path = config.endpoint.balances;

    const response = await this.dataRepositoryService.send(
      "GET",
      path,
      null,
      true,
      true,
      this.glossApiUrl,
    );

    return response as FiskilBalancesResponse;
  }

  /**
   * Fetch transactions via Gloss API (backend fetches from Fiskil).
   * @param accountId - Fiskil account ID (required for per-account fetch)
   * @param fromDate - Optional YYYY-MM-DD; when provided, returns transactions from that date onward (inclusive)
   */
  async getTransactions(
    accountId?: string,
    fromDate?: string,
  ): Promise<FiskilTransactionsResponse> {
    const config = getFiskilConfig();
    let path = accountId
      ? `${config.endpoint.accountTransactions}/${accountId}/transactions`
      : config.endpoint.transactions;
    if (accountId && fromDate) {
      path += `?from_date=${encodeURIComponent(fromDate)}`;
    }

    const response = await this.dataRepositoryService.send(
      "GET",
      path,
      null,
      true,
      true,
      this.glossApiUrl,
    );

    return response as FiskilTransactionsResponse;
  }
}
